import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config";
import {
  FiUser,
  FiPhone,
  FiMail,
  FiMap,
  FiCalendar,
  FiFileText,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiDownload,
  FiArrowLeft,
  FiHome,
  FiMapPin,
  FiFlag,
  FiMessageSquare,
  FiEdit,
  FiAward,
  FiNavigation,
  FiImage,
  FiEye,
  FiPlus,
  FiChevronRight,
  FiChevronLeft,
  FiTag,
  FiLayers,
  FiShare2,
  FiPrinter,
  FiStar,
  FiCopy,
  FiAlertCircle,
  FiLoader,
  FiTrendingUp,
  FiThumbsUp,
  FiActivity,
  FiRefreshCw
} from "react-icons/fi";
import { 
  BsWhatsapp,
  BsFillCameraFill,
  BsCalendarCheck,
  BsClockHistory,
  BsTelephone,
  BsEnvelope,
  BsGeoAlt,
  BsBuilding
} from "react-icons/bs";
import { 
  MdEmail,
  MdPhone,
  MdLocationOn,
  MdCalendarToday,
  MdVerifiedUser,
  MdOutlineDescription
} from "react-icons/md";
import { 
  FaRegClock,
  FaRegCalendarAlt,
  FaUserCheck,
  FaUserClock,
  FaUserCog,
  FaMapMarkedAlt
} from "react-icons/fa";

const VisitorDetails = () => {
  const { visitor_id } = useParams();
  const navigate = useNavigate();
  const [visitorData, setVisitorData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("personal");
  const [selectedImage, setSelectedImage] = useState(null);
  const [expandedRemarks, setExpandedRemarks] = useState({});
  const [showTimeline, setShowTimeline] = useState(false);

  useEffect(() => {
    setVisitorData(null);
    fetchVisitorData(visitor_id);
  }, [visitor_id]);

  const fetchVisitorData = async (visitorId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API_URL}getReportbystatus`,
        {
          status: visitorId,
          start_date: "",
          end_date: "",
          page: "1",
          perPage: "1000",
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (
        response.data.status === "success" &&
        response.data.data &&
        response.data.data.length > 0
      ) {
        const visitor = response.data.data[0];
        if (visitor.visitor_id.toString() === visitorId) {
          setVisitorData(visitor);
          setError("");
        } else {
          setError("Visitor data mismatch. Please try again.");
        }
      } else {
        setError("No details found for this visitor.");
      }
    } catch (error) {
      console.error("Error fetching visitor data:", error);
      setError("An error occurred while fetching the data.");
    } finally {
      setLoading(false);
    }
  };

  // Status configuration
  const statusConfig = {
    "open": {
      label: "Pending",
      color: "from-yellow-500 to-amber-500",
      icon: FiClock,
      bgColor: "bg-gradient-to-r from-yellow-50 to-amber-50",
      borderColor: "border-yellow-200",
      textColor: "text-yellow-800",
      badgeColor: "bg-yellow-100 text-yellow-800"
    },
    "on-going": {
      label: "In Progress",
      color: "from-blue-500 to-cyan-500",
      icon: FiActivity,
      bgColor: "bg-gradient-to-r from-blue-50 to-cyan-50",
      borderColor: "border-blue-200",
      textColor: "text-blue-800",
      badgeColor: "bg-blue-100 text-blue-800"
    },
    "close": {
      label: "Resolved",
      color: "from-green-500 to-emerald-500",
      icon: FiCheckCircle,
      bgColor: "bg-gradient-to-r from-green-50 to-emerald-50",
      borderColor: "border-green-200",
      textColor: "text-green-800",
      badgeColor: "bg-green-100 text-green-800"
    }
  };

  const currentStatus = visitorData?.status ? statusConfig[visitorData.status] : statusConfig.open;

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Format datetime for display
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Calculate time difference
  const getTimeAgo = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    return "Just now";
  };

  // Toggle remark expansion
  const toggleRemark = (remarkId) => {
    setExpandedRemarks(prev => ({
      ...prev,
      [remarkId]: !prev[remarkId]
    }));
  };

  // Copy tracking ID
  const copyTrackingId = () => {
    if (visitorData?.traking_id) {
      navigator.clipboard.writeText(visitorData.traking_id);
      // Show toast notification
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 animate-slideIn';
      toast.textContent = 'Tracking ID copied to clipboard!';
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 3000);
    }
  };

  // Open WhatsApp
  const openWhatsApp = () => {
    if (visitorData?.mobile_number) {
      window.open(`https://wa.me/${visitorData.mobile_number}`, '_blank');
    }
  };

  // Refresh data
  const handleRefresh = () => {
    fetchVisitorData(visitor_id);
  };

  // Print page
  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="relative inline-block">
            <div className="w-24 h-24 border-4 border-green-200 rounded-full"></div>
            <div className="absolute top-0 left-0 w-24 h-24 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <FiRefreshCw className="w-10 h-10 text-green-600 animate-pulse" />
            </div>
          </div>
          <p className="mt-8 text-gray-800 font-semibold text-xl animate-pulse">
            Loading Visitor Details...
          </p>
          <p className="text-gray-600 mt-3 max-w-md">
            Fetching comprehensive details and analytics
          </p>
          <div className="mt-8 w-64 h-2 bg-gradient-to-r from-green-200 to-blue-200 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-green-500 to-blue-500 animate-progress"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 text-center transform hover:scale-[1.01] transition-transform duration-500">
          <div className="w-24 h-24 bg-gradient-to-br from-red-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg animate-bounce">
            <FiXCircle className="w-12 h-12 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">
            Oops! Error Loading Data
          </h2>
          <p className="text-gray-600 mb-6 px-4">{error}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-xl"
            >
              <FiArrowLeft className="mr-2" /> Go Back
            </button>
            <button
              onClick={handleRefresh}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-xl hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-xl"
            >
              <FiRefreshCw className="mr-2" /> Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-blue-50 pb-12 print:bg-white print:from-white print:to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 shadow-2xl print:bg-gray-800 print:text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="flex items-start lg:items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-all duration-300 hover:scale-110 shadow-lg"
              >
                <FiArrowLeft className="w-6 h-6 text-white" />
              </button>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  <div className="px-4 py-1.5 bg-white/20 backdrop-blur-sm rounded-full shadow-lg">
                    <span className="text-white text-sm font-semibold tracking-wide">
                      Visitor #{visitorData.visitor_id}
                    </span>
                  </div>
                  <button
                    onClick={copyTrackingId}
                    className="px-4 py-1.5 bg-white/10 hover:bg-white/20 rounded-full flex items-center gap-2 transition-all duration-300 hover:scale-105 shadow-lg backdrop-blur-sm"
                  >
                    <span className="text-white text-sm font-semibold">
                      ID: {visitorData.traking_id}
                    </span>
                    <FiCopy className="w-3.5 h-3.5 text-white" />
                  </button>
                  <div className="hidden sm:block px-4 py-1.5 rounded-full backdrop-blur-sm shadow-lg bg-gradient-to-r from-purple-500/20 to-pink-500/20">
                    <span className="text-white text-sm font-semibold flex items-center gap-2">
                      <FiTag className="w-3.5 h-3.5" />
                      {visitorData.categorie_name || "No Category"}
                    </span>
                  </div>
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-2 leading-tight">
                  {visitorData.salutation ? `${visitorData.salutation} ` : ""}
                  {visitorData.visitor_name}
                </h1>
                <div className="flex items-center gap-3 text-blue-100">
                  <div className="flex items-center gap-2">
                    <FiStar className="w-4 h-4" />
                    <span className="text-sm">VIP Visitor</span>
                  </div>
                  <span className="text-blue-200">•</span>
                  <span className="text-sm">{getTimeAgo(visitorData.updated_at)}</span>
                </div>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-3 mt-4 lg:mt-0">
              <button 
                onClick={handleRefresh}
                className="px-4 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center gap-2 shadow-lg"
              >
                <FiRefreshCw className="w-5 h-5" />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {visitorData && (
        <div className="max-w-7xl mx-auto px-4 py-8 -mt-8 relative print:mt-0 print:py-4">
          
         

          {/* Timeline Section */}
          {showTimeline && (
            <div className="mb-8 bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-3">
                <FaRegClock className="w-6 h-6 text-blue-600" />
                Visitor Timeline
              </h3>
              <div className="relative pl-8">
                {/* Timeline line */}
                <div className="absolute left-7 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-400 via-green-400 to-purple-400"></div>
                
                {/* Timeline items */}
                <div className="space-y-8">
                  <TimelineItem 
                    date={visitorData.created_at}
                    title="Visitor Created"
                    description="Visitor record was created"
                    icon={FaRegCalendarAlt}
                    color="bg-blue-500"
                  />
                  {visitorData.updated_at !== visitorData.created_at && (
                    <TimelineItem 
                      date={visitorData.updated_at}
                      title="Last Updated"
                      description="Visitor information was updated"
                      icon={FiRefreshCw}
                      color="bg-green-500"
                    />
                  )}
                  {visitorData.problem_resolvedate && (
                    <TimelineItem 
                      date={visitorData.problem_resolvedate}
                      title="Issue Resolved"
                      description="Visitor's problem was resolved"
                      icon={FiCheckCircle}
                      color="bg-purple-500"
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Profile & Quick Info */}
            <div className="lg:col-span-1 space-y-6">
              {/* Profile Card */}
              <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl overflow-hidden border border-gray-200/50 hover:shadow-2xl transition-all duration-500">
                <div className="p-6">
                  <div className="flex flex-col items-center">
                    <div className="relative mb-6">
                      <div className="w-40 h-40 bg-gradient-to-br from-green-400 via-emerald-500 to-teal-600 rounded-full flex items-center justify-center shadow-2xl mb-4 animate-pulse">
                        <div className="w-32 h-32 bg-white/20 rounded-full flex items-center justify-center">
                          <FiUser className="w-20 h-20 text-white" />
                        </div>
                      </div>
                      {visitorData.whatsapp_tick === 1 && (
                        <div className="absolute bottom-6 right-6 w-14 h-14 bg-gradient-to-br from-[#25D366] to-[#128C7E] rounded-full flex items-center justify-center shadow-xl border-4 border-white animate-bounce">
                          <BsWhatsapp className="w-7 h-7 text-white" />
                        </div>
                      )}
                      <div className="absolute top-6 left-6">
                        <div className={`px-4 py-2 rounded-full ${currentStatus.badgeColor} font-semibold shadow-lg`}>
                          {currentStatus.label}
                        </div>
                      </div>
                    </div>
                    
                    <h2 className="text-2xl font-bold text-gray-800 mb-3 text-center">
                      {visitorData.salutation ? `${visitorData.salutation} ` : ""}
                      {visitorData.visitor_name}
                    </h2>
                    
                    <div className="flex items-center gap-3 mb-6">
                      <div className="px-4 py-2 bg-gradient-to-r from-amber-50 to-orange-50 rounded-full flex items-center gap-2 shadow-md">
                        <FiStar className="w-5 h-5 text-amber-500" />
                        <span className="font-semibold text-gray-700">{visitorData.categorie_name}</span>
                      </div>
                    </div>
                  </div>

                  {/* Quick Info Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-1 gap-4 mt-6">
                    <InfoCard
                      icon={BsTelephone}
                      title="Phone"
                      value={visitorData.mobile_number}
                      color="from-green-50 to-emerald-50"
                      iconColor="text-green-600"
                      iconBg="bg-green-100"
                    />
                    
                    <InfoCard
                      icon={BsEnvelope}
                      title="Email"
                      value={visitorData.email_id || "Not Available"}
                      color="from-blue-50 to-cyan-50"
                      iconColor="text-blue-600"
                      iconBg="bg-blue-100"
                      highlight={!visitorData.email_id}
                    />
                    
                    <InfoCard
                      icon={MdCalendarToday}
                      title="Date of Birth"
                      value={formatDate(visitorData.dob)}
                      color="from-amber-50 to-orange-50"
                      iconColor="text-amber-600"
                      iconBg="bg-amber-100"
                    />
                    
                    <InfoCard
                      icon={BsCalendarCheck}
                      title="Created"
                      value={formatDate(visitorData.created_at)}
                      color="from-purple-50 to-pink-50"
                      iconColor="text-purple-600"
                      iconBg="bg-purple-100"
                    />
                  </div>
                </div>
              </div>

              {/* Office Info */}
              <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-6 border border-gray-200/50 hover:shadow-2xl transition-all duration-500">
                <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center shadow-md">
                    <BsBuilding className="w-6 h-6 text-indigo-600" />
                  </div>
                  Office Information
                </h3>
                <div className="space-y-5">
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-2xl border border-gray-200">
                    <p className="text-sm text-gray-500 font-medium mb-1">Office Name</p>
                    <p className="font-bold text-gray-800 text-lg">{visitorData.office?.name || "Lucknow Office"}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Details & Remarks */}
            <div className="lg:col-span-2 space-y-6">
              {/* Tab Navigation */}
              <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl overflow-hidden border border-gray-200/50">
                <div className="border-b border-gray-100">
                  <div className="flex overflow-x-auto scrollbar-hide">
                    {["personal", "location", "request", "remarks"].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`flex-1 min-w-max px-6 py-5 text-center font-semibold transition-all duration-300 relative group ${
                          activeTab === tab
                            ? `text-green-600 ${currentStatus.bgColor}`
                            : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {tab === "personal" && (
                          <>
                            <FiUser className="w-5 h-5 mx-auto mb-2" />
                            Personal Info
                          </>
                        )}
                        {tab === "location" && (
                          <>
                            <FiMapPin className="w-5 h-5 mx-auto mb-2" />
                            Location
                          </>
                        )}
                        {tab === "request" && (
                          <>
                            <FiFileText className="w-5 h-5 mx-auto mb-2" />
                            Request
                          </>
                        )}
                        {tab === "remarks" && (
                          <>
                            <FiMessageSquare className="w-5 h-5 mx-auto mb-2" />
                            Remarks ({visitorData.remarks?.length || 0})
                          </>
                        )}
                        {activeTab === tab && (
                          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 to-emerald-500"></div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="p-6">
                  {/* Personal Info Tab */}
                  {activeTab === "personal" && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <InfoSection
                          icon={FiUser}
                          title="Basic Information"
                          iconColor="text-green-600"
                          iconBg="bg-green-100"
                          gradient="from-green-50 to-emerald-50"
                        >
                          <InfoItem label="Full Name" value={`${visitorData.salutation || ''} ${visitorData.visitor_name}`.trim()} />
                          <InfoItem label="Category" value={visitorData.categorie_name} />
                          <InfoItem label="DOB" value={formatDate(visitorData.dob)} />
                          <InfoItem label="WhatsApp Verified" value={visitorData.whatsapp_tick === 1 ? "✓ Yes" : "✗ No"} />
                        </InfoSection>

                        <InfoSection
                          icon={BsEnvelope}
                          title="Contact Information"
                          iconColor="text-blue-600"
                          iconBg="bg-blue-100"
                          gradient="from-blue-50 to-cyan-50"
                        >
                          <InfoItem 
                            label="Mobile" 
                            value={visitorData.mobile_number}
                            icon={<FiPhone className="w-4 h-4 text-blue-500" />}
                          />
                          <InfoItem 
                            label="Email" 
                            value={visitorData.email_id || "Not Available"}
                            icon={<MdEmail className="w-4 h-4 text-blue-500" />}
                            highlight={!visitorData.email_id}
                          />
                          <InfoItem 
                            label="Alternate Phone" 
                            value={visitorData.whatsappNumber || "Same as Mobile"}
                          />
                        </InfoSection>
                      </div>

                      <InfoSection
                        icon={FiMessageSquare}
                        title="Query Details"
                        iconColor="text-purple-600"
                        iconBg="bg-purple-100"
                        gradient="from-purple-50 to-pink-50"
                      >
                        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-inner">
                          <div className="flex items-start gap-3">
                            <FiMessageSquare className="w-6 h-6 text-purple-500 mt-1 flex-shrink-0" />
                            <div>
                              <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">
                                {visitorData.query || "No query provided by the visitor."}
                              </p>
                              {!visitorData.query && (
                                <p className="text-gray-500 text-sm mt-2 italic">
                                  The visitor did not provide any specific query.
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </InfoSection>
                    </div>
                  )}

                  {/* Location Tab */}
                  {activeTab === "location" && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <InfoSection
                          icon={FiMap}
                          title="Geographical Information"
                          iconColor="text-green-600"
                          iconBg="bg-green-100"
                          gradient="from-green-50 to-emerald-50"
                        >
                          <InfoItem 
                            label="State" 
                            value={visitorData.state || "Not Specified"} 
                            icon={<FiFlag className="w-4 h-4 text-green-500" />}
                          />
                          <InfoItem label="District" value={visitorData.district || "Not Specified"} />
                          <InfoItem label="Assembly" value={visitorData.assembly || "Not Specified"} />
                          <InfoItem label="Address" value={visitorData.address || "Not Provided"} />
                        </InfoSection>

                        <InfoSection
                          icon={FaMapMarkedAlt}
                          title="Location Details"
                          iconColor="text-blue-600"
                          iconBg="bg-blue-100"
                          gradient="from-blue-50 to-cyan-50"
                        >
                          <div className="h-64 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-2xl flex flex-col items-center justify-center p-6 border-2 border-dashed border-blue-200">
                            <MdLocationOn className="w-16 h-16 text-blue-400 mb-4" />
                            <p className="text-gray-700 font-semibold text-lg text-center mb-2">
                              {visitorData.state || "Location"}
                            </p>
                            <p className="text-gray-600 text-center">
                              {visitorData.district && `${visitorData.district}, `}{visitorData.assembly}
                            </p>
                            {visitorData.address && (
                              <p className="text-gray-500 text-sm text-center mt-3">
                                {visitorData.address}
                              </p>
                            )}
                          </div>
                        </InfoSection>
                      </div>
                    </div>
                  )}

                  {/* Request Tab */}
                  {activeTab === "request" && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <InfoSection
                          icon={MdOutlineDescription}
                          title="Request Information"
                          iconColor="text-green-600"
                          iconBg="bg-green-100"
                          gradient="from-green-50 to-emerald-50"
                        >
                          <InfoItem label="To Meet" value={visitorData.to_meet || "Not Specified"} />
                          <InfoItem label="Visit Purpose" value={visitorData.visit_purpose || "Not Specified"} />
                          <InfoItem label="IGRS Number" value={visitorData.igrs_number || "Not Provided"} />
                          <InfoItem label="Out Date" value={formatDateTime(visitorData.out_date)} />
                        </InfoSection>

                        <InfoSection
                          icon={BsClockHistory}
                          title="Timeline & Status"
                          iconColor="text-blue-600"
                          iconBg="bg-blue-100"
                          gradient="from-blue-50 to-cyan-50"
                        >
                          <InfoItem label="Created" value={formatDateTime(visitorData.created_at)} />
                          <InfoItem label="Updated" value={formatDateTime(visitorData.updated_at)} />
                          {visitorData.problem_resolvedate && (
                            <InfoItem 
                              label="Resolved" 
                              value={formatDateTime(visitorData.problem_resolvedate)}
                              highlight={true}
                            />
                          )}
                          <div className="mt-6">
                            <div className={`px-4 py-3 rounded-xl ${currentStatus.bgColor} border ${currentStatus.borderColor}`}>
                              <div className="flex items-center gap-3">
                                {currentStatus.icon && React.createElement(currentStatus.icon, { className: `w-5 h-5 ${currentStatus.textColor}` })}
                                <span className={`font-semibold ${currentStatus.textColor}`}>
                                  Current Status: {currentStatus.label}
                                </span>
                              </div>
                            </div>
                          </div>
                        </InfoSection>
                      </div>
                    </div>
                  )}

                  {/* Remarks Tab */}
                  {activeTab === "remarks" && (
                    <div className="space-y-6">
                      {visitorData.remarks && visitorData.remarks.length > 0 ? (
                        <>
                          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <div>
                              <h4 className="font-bold text-gray-800 text-2xl mb-2">
                                Visitor Remarks
                              </h4>
                              <p className="text-gray-600">
                                {visitorData.remarks.length} remark{visitorData.remarks.length !== 1 ? 's' : ''} found
                              </p>
                            </div>
                            
                          </div>

                          <div className="space-y-5">
                            {visitorData.remarks.map((remark, index) => (
                              <div 
                                key={remark.id} 
                                className="bg-gradient-to-r from-white to-gray-50 rounded-2xl shadow-xl border border-gray-200 overflow-hidden hover:shadow-2xl transition-all duration-500"
                              >
                                <div 
                                  className="p-6 cursor-pointer hover:bg-gray-50/50 transition-colors duration-300"
                                  onClick={() => toggleRemark(remark.id)}
                                >
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <div className="flex items-start gap-4 mb-4">
                                        <div className="w-14 h-14 bg-gradient-to-br from-green-100 to-emerald-100 rounded-xl flex items-center justify-center shadow-md">
                                          <FiMessageSquare className="w-7 h-7 text-green-600" />
                                        </div>
                                        <div className="flex-1">
                                          <div className="flex flex-wrap items-center gap-3 mb-2">
                                            <h5 className="font-bold text-gray-800 text-lg">
                                              Remark #{index + 1}
                                            </h5>
                                            <span className="px-3 py-1 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 text-xs font-semibold rounded-full">
                                              {getTimeAgo(remark.created_at)}
                                            </span>
                                          </div>
                                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                                            <span className="flex items-center gap-1">
                                              <FiCalendar className="w-4 h-4" />
                                              {formatDate(remark.created_at)}
                                            </span>
                                            <span className="flex items-center gap-1">
                                              <FiClock className="w-4 h-4" />
                                              {formatDateTime(remark.created_at).split(',')[1]?.trim()}
                                            </span>
                                            {remark.images?.length > 0 && (
                                              <span className="flex items-center gap-1">
                                                <FiImage className="w-4 h-4" />
                                                {remark.images.length} image{remark.images.length !== 1 ? 's' : ''}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                      <p className={`text-gray-700 leading-relaxed ${!expandedRemarks[remark.id] ? 'line-clamp-3' : ''}`}>
                                        {remark.remark}
                                      </p>
                                    </div>
                                    <div className="ml-4">
                                      <FiChevronRight className={`w-6 h-6 text-gray-400 transition-transform duration-300 ${
                                        expandedRemarks[remark.id] ? 'rotate-90' : ''
                                      }`} />
                                    </div>
                                  </div>
                                </div>

                                {/* Expanded content with images */}
                                {expandedRemarks[remark.id] && remark.images && remark.images.length > 0 && (
                                  <div className="p-6 border-t border-gray-100 bg-gray-50/50">
                                    <h6 className="font-semibold text-gray-700 mb-4 flex items-center gap-3">
                                      <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-lg flex items-center justify-center">
                                        <FiImage className="w-5 h-5 text-blue-600" />
                                      </div>
                                      Attached Images ({remark.images.length})
                                    </h6>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                      {remark.images.map((image, imgIndex) => (
                                        <div 
                                          key={image.id}
                                          className="relative group cursor-pointer"
                                          onClick={() => setSelectedImage(image.image_path)}
                                        >
                                          <img
                                            src={image.image_path}
                                            alt={`Remark ${index + 1} - Image ${imgIndex + 1}`}
                                            className="w-full h-48 object-cover rounded-xl shadow-lg group-hover:shadow-2xl transition-all duration-500 transform group-hover:scale-[1.02]"
                                          />
                                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center p-4">
                                            <div className="flex items-center gap-2 text-white">
                                              <FiEye className="w-5 h-5" />
                                              <span className="text-sm font-medium">View Image</span>
                                            </div>
                                          </div>
                                          <div className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                                            {imgIndex + 1}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-12">
                          <div className="w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
                            <FiMessageSquare className="w-16 h-16 text-gray-400" />
                          </div>
                          <h4 className="text-2xl font-bold text-gray-700 mb-3">
                            No Remarks Yet
                          </h4>
                          <p className="text-gray-500 mb-8 max-w-md mx-auto">
                            This visitor doesn't have any remarks yet. Add the first remark to track updates, progress, and communication.
                          </p>
                          <button className="px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center gap-3 mx-auto shadow-lg">
                            <FiPlus className="w-6 h-6" />
                            Add First Remark
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-5xl max-h-[90vh] w-full">
            <button
              className="absolute top-4 right-4 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all duration-300 hover:scale-125 shadow-lg"
              onClick={() => setSelectedImage(null)}
            >
              <FiXCircle className="w-7 h-7 text-white" />
            </button>
            <div className="bg-black/50 rounded-xl p-2">
              <img
                src={selectedImage}
                alt="Preview"
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Reusable Components
const InfoCard = ({ icon: Icon, title, value, color, iconColor, iconBg, highlight = false }) => (
  <div className={`bg-gradient-to-br ${color} p-4 rounded-2xl border border-gray-200 hover:shadow-lg transition-all duration-300`}>
    <div className="flex items-center gap-3 mb-3">
      <div className={`w-12 h-12 ${iconBg} rounded-xl flex items-center justify-center shadow-md`}>
        <Icon className={`w-6 h-6 ${iconColor}`} />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className={`font-bold text-lg truncate ${highlight ? 'text-red-500' : 'text-gray-800'}`}>
          {value}
        </p>
      </div>
    </div>
  </div>
);

const InfoSection = ({ icon: Icon, title, iconColor, iconBg, gradient, children }) => (
  <div className={`bg-gradient-to-br ${gradient} p-6 rounded-3xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-500`}>
    <div className="flex items-center gap-3 mb-6">
      <div className={`w-14 h-14 ${iconBg} rounded-2xl flex items-center justify-center shadow-lg`}>
        <Icon className={`w-7 h-7 ${iconColor}`} />
      </div>
      <h3 className="text-xl font-bold text-gray-800">{title}</h3>
    </div>
    <div className="space-y-4">
      {children}
    </div>
  </div>
);

const InfoItem = ({ label, value, icon, highlight = false }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-200/50 last:border-0">
    <div className="flex items-center gap-3">
      {icon}
      <span className="text-gray-600 font-medium">{label}:</span>
    </div>
    <span className={`font-semibold text-right ${highlight ? 'text-green-600' : 'text-gray-800'}`}>
      {value || "N/A"}
    </span>
  </div>
);

const TimelineItem = ({ date, title, description, icon: Icon, color }) => (
  <div className="relative flex items-start gap-4">
    <div className={`w-14 h-14 ${color} rounded-full flex items-center justify-center shadow-lg z-10 flex-shrink-0`}>
      <Icon className="w-7 h-7 text-white" />
    </div>
    <div className="flex-1 pt-2">
      <h4 className="font-bold text-gray-800 text-lg mb-1">{title}</h4>
      <p className="text-gray-600 mb-2">{description}</p>
      <p className="text-sm text-gray-500">{formatDateTime(date)}</p>
    </div>
  </div>
);

// Helper function for formatDateTime (defined above)
const formatDateTime = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default VisitorDetails;